import { API } from '@pibex/shared';
import type { MeResponse, User, UserStatus } from '@pibex/shared';
import { useId, useState } from 'react';
import { Avatar } from '../../components/Avatar.js';
import { Badge } from '../../components/Badge.js';
import { Notice } from '../../components/Notice.js';
import { es } from '../../i18n/es.js';
import { api, isApiError } from '../../lib/api.js';
import { formatDateTime } from '../../lib/trip.js';

const t = es.adminPage.members;

type Feedback = { tone: 'success' | 'error'; text: string } | null;

interface MembersListProps {
  members: User[];
  onChanged(): Promise<void> | void;
}

export function MembersList({ members, onChanged }: MembersListProps) {
  const id = useId();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const toggle = async (member: User) => {
    const status: UserStatus = member.status === 'active' ? 'disabled' : 'active';
    setBusyId(member.id);
    setFeedback(null);
    try {
      const { user } = await api.patch<MeResponse>(API.adminMember(member.id), { status });
      setFeedback({ tone: 'success', text: t.updated(user.name ?? user.email, user.status) });
      await onChanged();
    } catch (err) {
      if (isApiError(err, 'conflict')) setFeedback({ tone: 'error', text: t.adminLocked });
      else if (isApiError(err)) setFeedback({ tone: 'error', text: t.conflict });
      else setFeedback({ tone: 'error', text: es.networkError });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section aria-labelledby={`${id}-heading`} className="flex flex-col gap-3">
      <h2 id={`${id}-heading`} className="text-lg font-semibold tracking-tight">
        {t.heading}
      </h2>
      <div aria-live="polite">
        {feedback ? <Notice tone={feedback.tone}>{feedback.text}</Notice> : null}
      </div>
      {members.length === 0 ? (
        <p className="rounded-md border border-dashed border-neutral-300 px-4 py-6 text-center text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
          {t.empty}
        </p>
      ) : (
        <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {members.map((m) => {
            const isAdmin = m.role === 'admin';
            const active = m.status === 'active';
            const label = m.name ?? m.email;
            return (
              <li key={m.id} className="flex items-center gap-3 py-3">
                <Avatar name={m.name} email={m.email} pictureUrl={m.pictureUrl} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{label}</p>
                  {m.name ? (
                    <p className="text-sm break-all text-neutral-500 dark:text-neutral-400">
                      {m.email}
                    </p>
                  ) : null}
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {m.lastLoginAt ? t.lastLogin(formatDateTime(m.lastLoginAt)) : t.neverLoggedIn}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <Badge tone={isAdmin ? 'accent' : 'neutral'}>{t.role[m.role]}</Badge>
                    <Badge tone={active ? 'success' : 'neutral'}>{t.status[m.status]}</Badge>
                  </div>
                </div>
                <StatusToggle
                  active={active}
                  label={t.toggleLabel(label)}
                  disabled={isAdmin || busyId === m.id}
                  title={isAdmin ? t.adminLocked : undefined}
                  onToggle={() => void toggle(m)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface StatusToggleProps {
  active: boolean;
  label: string;
  disabled: boolean;
  title?: string;
  onToggle(): void;
}

function StatusToggle({ active, label, disabled, title, onToggle }: StatusToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-label={label}
      title={title}
      disabled={disabled}
      onClick={onToggle}
      className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        aria-hidden="true"
        className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
          active ? 'bg-accent' : 'bg-neutral-300 dark:bg-neutral-700'
        }`}
      >
        <span
          className={`inline-block size-5 rounded-full bg-white shadow-sm transition-transform ${
            active ? 'translate-x-4.5' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
}
